import type React from 'react';
import { type ReactNode, useLayoutEffect, useRef } from 'react';
import type { Section } from '../../config/types';

interface SectionListWrapperProps {
  showSections: boolean;
  section: Section;
  sectionIndex: number;
  children: ReactNode;
  sectionAction?: ReactNode;
}
export const SectionListWrapper: React.FC<SectionListWrapperProps> = ({
  showSections,
  section,
  sectionIndex,
  children,
  sectionAction,
}) => {
  const headingRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLSpanElement>(null);
  const hasSectionAction = !!sectionAction;
  useLayoutEffect(() => {
    const heading = headingRef.current;
    const title = titleRef.current;
    if (!heading || !title || !hasSectionAction) return;
    const positionAction = () => {
      heading.style.setProperty(
        '--section-title-end',
        `${title.getBoundingClientRect().right - heading.getBoundingClientRect().left}px`,
      );
    };
    positionAction();
    const observer = new ResizeObserver(positionAction);
    observer.observe(heading);
    void document.fonts?.ready.then(positionAction);
    return () => observer.disconnect();
  }, [hasSectionAction, showSections, section.title]);

  if (showSections) {
    const hasFastToggle = section.subsections.some((subsection) =>
      subsection.pages.some((page) => page.fastToggle),
    );
    return (
      <li
        key={section.slug}
        data-testid="overview-sectionList-section"
        className={hasFastToggle ? 'app-task-list__section--nil-return' : undefined}
      >
        <div
          ref={headingRef}
          className={hasFastToggle ? 'app-task-list__section-heading' : undefined}
        >
          <h2 className="govuk-heading-m" id={`section-${section.slug}`}>
            <span className="app-task-list__section-number">{sectionIndex + 1}.</span>
            <span ref={titleRef}>{section.title}</span>
          </h2>
          {sectionAction}
        </div>
        {section.subsections && <ol className="app-task-list__items">{children}</ol>}
      </li>
    );
  }
  return <>{children}</>;
};
interface DeclarationWrapperProps {
  showSections: boolean;
  children: ReactNode;
  sections: Section[];
  titleOverride?: string;
}
export const DeclarationWrapper: React.FC<DeclarationWrapperProps> = ({
  showSections,
  children,
  sections,
  titleOverride,
}) => {
  if (showSections) {
    return (
      <li>
        <h2 className="govuk-heading-m">
          <span className="app-task-list__section-number">{sections.length + 1}.</span>
          {titleOverride || 'Declaration'}
        </h2>
        <ol className="app-task-list__items">{children}</ol>
      </li>
    );
  }
  return <>{children}</>;
};
